/* eslint-disable indent */
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import { getTranslation } from '../services/translations'
import {
  createBundle,
  deleteBundle,
  initializeBundles,
} from '../reducers/bundleReducer'

const MergeListsModal = ({ show, onHide, candidates, onBundlesChanged }) => {
  const dispatch = useDispatch()
  const user = useSelector(state => state.user)
  const bundles = useSelector(state => state.bundles.allBundles)
  const lang = (user && user.language) || 'fi'
  const t = key => getTranslation(lang, key)

  const [selectedIds, setSelectedIds] = useState([])
  const [message, setMessage] = useState({ type: '', text: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Ladataan niput kun modaali avataan
  useEffect(() => {
    if (show) {
      const token = window.localStorage.getItem('loggedUser')
      if (token) {
        dispatch(initializeBundles(JSON.parse(token)))
      }
      setSelectedIds([])
      setMessage({ type: '', text: '' })
    }
  }, [show, dispatch])

  const toggleCandidate = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  // Laske mitkä listat ovat jo niputettuja
  const getBundleForCandidate = candidateId => {
    return bundles.find(b =>
      b.candidates.some(c => (c.id || c._id || c) === candidateId)
    )
  }

  const handleMerge = async () => {
    if (selectedIds.length < 2) {
      setMessage({
        type: 'warning',
        text:
          lang === 'fi'
            ? 'Valitse vähintään 2 listaa yhdistettäväksi.'
            : 'Select at least 2 lists to merge.',
      })
      return
    }

    setIsSubmitting(true)
    setMessage({ type: '', text: '' })

    try {
      const token = window.localStorage.getItem('loggedUser')
      await dispatch(createBundle(selectedIds, JSON.parse(token)))
      setMessage({
        type: 'success',
        text:
          lang === 'fi'
            ? 'Listat yhdistetty onnistuneesti!'
            : 'Lists merged successfully!',
      })
      setSelectedIds([])
      // Force reload bundles
      const tokenParsed = JSON.parse(window.localStorage.getItem('loggedUser'))
      const response = await fetch('/api/bundles', {
        headers: { 'x-oauth-token': tokenParsed },
      })
      const data = await response.json()
      dispatch({ type: 'bundles/setAllBundles', payload: data })

      if (onBundlesChanged) onBundlesChanged()
    } catch (error) {
      setMessage({
        type: 'danger',
        text: lang === 'fi' ? 'Yhdistäminen epäonnistui.' : 'Merge failed.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUnbundle = async bundleId => {
    try {
      const token = window.localStorage.getItem('loggedUser')
      await dispatch(deleteBundle(bundleId, JSON.parse(token)))
      // Lataa uudelleen
      const tokenParsed = JSON.parse(window.localStorage.getItem('loggedUser'))
      const response = await fetch('/api/bundles', {
        headers: { 'x-oauth-token': tokenParsed },
      })
      const data = await response.json()
      dispatch({ type: 'bundles/setAllBundles', payload: data })

      if (onBundlesChanged) onBundlesChanged()
      setMessage({
        type: 'success',
        text: lang === 'fi' ? 'Niputus purettu.' : 'Bundle removed.',
      })
    } catch (error) {
      setMessage({
        type: 'danger',
        text:
          lang === 'fi' ? 'Niputuksen purku epäonnistui.' : 'Unbundle failed.',
      })
    }
  }

  // Muodosta niputuksen nimi: "GK, WB ja CD"
  const bundleName = bundle => {
    const names = bundle.candidates.map(c => c.Name || c)
    if (names.length === 0) return ''
    if (names.length === 1) return names[0]
    return names.slice(0, -1).join(', ') + ' ja ' + names[names.length - 1]
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header
        closeButton
        style={{
          backgroundColor: '#23272A',
          color: '#FFFFFF',
          borderColor: '#1E2124',
        }}
      >
        <Modal.Title>
          {lang === 'fi' ? 'Yhdistä listoja' : 'Merge lists'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: '#2C2F33', color: '#FFFFFF' }}>
        {message.text && (
          <Alert
            variant={message.type}
            dismissible
            onClose={() => setMessage({ type: '', text: '' })}
          >
            {message.text}
          </Alert>
        )}

        {/* Olemassa olevat niputukset */}
        {bundles.length > 0 && (
          <div className="mb-4">
            <h6>{lang === 'fi' ? 'Nykyiset niputukset' : 'Current bundles'}</h6>
            <Table variant="dark" size="sm" bordered>
              <thead>
                <tr>
                  <th>{lang === 'fi' ? 'Niputus' : 'Bundle'}</th>
                  <th style={{ width: '100px' }}></th>
                </tr>
              </thead>
              <tbody>
                {bundles.map(bundle => (
                  <tr key={bundle.id}>
                    <td>{bundleName(bundle)}</td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleUnbundle(bundle.id)}
                      >
                        {lang === 'fi' ? 'Pura' : 'Remove'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Listojen valinta niputettavaksi */}
        <h6>
          {lang === 'fi'
            ? 'Valitse yhdistettävät listat'
            : 'Select lists to merge'}
        </h6>
        <Table variant="dark" size="sm" bordered hover>
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>{t('name')}</th>
              <th>Tyyppi</th>
              <th>Kisa/Kausi</th>
              <th>{lang === 'fi' ? 'Niputus' : 'Bundle'}</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map(candidate => {
              const candidateId = candidate.id || candidate._id
              const existingBundle = getBundleForCandidate(candidateId)
              const competitionSeasonText =
                candidate.Competition && candidate.Season
                  ? `${candidate.Competition} ${candidate.Season}`
                  : candidate.Competition || candidate.Season || ''
              return (
                <tr
                  key={candidateId}
                  onClick={() => toggleCandidate(candidateId)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedIds.includes(candidateId)
                      ? '#3a3f44'
                      : undefined,
                  }}
                >
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedIds.includes(candidateId)}
                      onChange={() => toggleCandidate(candidateId)}
                      onClick={e => e.stopPropagation()}
                    />
                  </td>
                  <td>{candidate.Name}</td>
                  <td>{candidate.TeamType}</td>
                  <td>{competitionSeasonText}</td>
                  <td>
                    {existingBundle ? (
                      <Badge bg="info">{bundleName(existingBundle)}</Badge>
                    ) : (
                      <span style={{ color: '#888' }}>-</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer
        style={{ backgroundColor: '#23272A', borderColor: '#1E2124' }}
      >
        <Button variant="secondary" onClick={onHide}>
          {lang === 'fi' ? 'Sulje' : 'Close'}
        </Button>
        <Button
          variant="info"
          onClick={handleMerge}
          disabled={isSubmitting || selectedIds.length < 2}
        >
          {isSubmitting
            ? lang === 'fi'
              ? 'Yhdistetään...'
              : 'Merging...'
            : lang === 'fi'
            ? `Yhdistä (${selectedIds.length})`
            : `Merge (${selectedIds.length})`}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default MergeListsModal
